import { LightningAutoplayButton } from '@components/LightningAutoplayButton/LightningAutoplayButton';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { Skeleton } from '@mui/material';
import { AppPage, MOMENTS_HEADER } from '@pages/AppPage';
import {
  AddMediaModal,
  AddMediaModalRef,
} from '@pages/Libraries/components/AddMediaModal/AddMediaModal';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MomentGrid } from '../components/MomentGrid/MomentGrid';
import { SearchBar } from '../components/SearchBar/SearchBar';
import { SearchHeader } from '../components/SearchHeader/SearchHeader';
import { SearchParamsInURL } from '../components/SearchParamsInURL/SearchParamsInURL';
import { NoMomentsFound } from './components/NoMomentsFound/NoMomentsFound';
import { TranscriptSearchSuggestion } from './components/TranscriptSearchSuggestion/TranscriptSearchSuggestion';
import css from './SearchPage.module.scss';

const LightningMenu = observer(function LightningMenu() {
  const uiPreferences = useUIPreferences();

  return (
    <div className={css.lightningWrapper}>
      <LightningAutoplayButton
        type="moments"
        autoplay={uiPreferences.autoplaymoments}
        onSelect={(enabled) => uiPreferences.setAutoplayMoments(enabled)}
      />
    </div>
  );
});

// Stand-in for the headline + subheadline while library stats load. Which copy
// variant is correct depends on whether the user has any media of their own, so
// the lines cannot be rendered early; without a placeholder the search bar would
// centre on its own and then drop as the text arrived. Sized to two lines of
// title, which is what the copy actually measures at most widths — see
// `.emptyStateCopy`, which reserves the height either way.
//
// `variant="rounded"` is load-bearing: the default `text` variant applies a
// `transform: scale(1, 0.6)` that the CSS module does not override, so the
// heights below would render at 60%.
const EmptyStateCopySkeletons = () => (
  <>
    <Skeleton variant="rounded" className={css.titleSkeleton} />
    <Skeleton variant="rounded" className={css.subtitleSkeleton} />
  </>
);

const _SearchPage = observer(function _SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const libraryManager = useLibraryManager();
  const uiPreferences = useUIPreferences();
  const isMobile = useIsMobile();

  const librarySearch = libraryManager.multiLibSearch;

  const hasSearchTerms = librarySearch.hasSearchTerms;
  const isUpdating = librarySearch.anyLibrarySearching;
  const hasResults = librarySearch.results?.results?.length;
  const isSelectingMoments = libraryManager.multiLibSearch.momentSelection.isSelecting;

  const isSearchBarHidden = !uiPreferences.showSearchBarWhileSelectingMoments && isSelectingMoments;
  const addMediaModalRef = useRef<AddMediaModalRef>(null);
  // Which library the Add-media modal should target, when the empty state can name
  // one. Undefined lets the modal pick the default library.
  const [addMediaTarget, setAddMediaTarget] = useState<string | undefined>(undefined);
  const wasHiddenRef = useRef(isSearchBarHidden);
  const cameFromWorkspace = useRef(searchParams.get('from') === 'workspace');
  const wasSearchActiveRef = useRef(false);

  // Reset when the user navigates to /search explicitly (e.g. via nav bar)
  useEffect(() => {
    cameFromWorkspace.current = searchParams.get('from') === 'workspace';
  }, [searchParams]);

  useEffect(() => {
    if (hasSearchTerms) {
      wasSearchActiveRef.current = true;
    } else if (wasSearchActiveRef.current && cameFromWorkspace.current) {
      navigate('/workspace');
    }
  }, [hasSearchTerms, navigate]);

  useEffect(() => {
    if (!hasSearchTerms) {
      librarySearch.momentSelection.clearSelection();
    }
  }, [hasSearchTerms]);

  useEffect(() => {
    // If SearchBar was hidden and is now visible, focus it
    if (wasHiddenRef.current && !isSearchBarHidden) {
      uiPreferences.searchBarFocusCallback?.();
    }
    wasHiddenRef.current = isSearchBarHidden;
  }, [isSearchBarHidden, uiPreferences]);

  // Adaptive empty-state copy. First-time users have no media of their own, so
  // the old "Search the Demo library…" line was really first-timer copy shown to
  // everyone. Split it: teach + nudge the demo for newcomers, get-to-work for
  // returning users who have their own media.
  //
  // Held behind `areLibraryStatsLoaded` deliberately: `isFirstTimeUser` counts
  // media, so before the library list lands it says "first time" for everyone,
  // and on a hard load of /search that would flash the demo nudge at an
  // established user. Skeletons stand in rather than nothing, and `.emptyStateCopy`
  // reserves the block's height, so the search bar below stays put. Don't
  // collapse this back to a bare read of the flag.
  const statsLoaded = libraryManager.areLibraryStatsLoaded;
  const isFirstTime = libraryManager.isFirstTimeUser;

  return (
    <div className={css.searchContainer}>
      {isMobile && <LightningMenu />}
      {!hasSearchTerms && (
        <div className={css.emptyStateCopy}>
          {statsLoaded ? (
            <>
              <span className={css.title}>
                {isFirstTime
                  ? 'Find any moment — just describe it.'
                  : 'Find any moment in seconds, not hours.'}
              </span>
              <span className={css.subtitle}>
                {isFirstTime
                  ? "Try it on the demo library: search by what's shown or what's said."
                  : "Search by what's shown and what's said, across your libraries."}
              </span>
            </>
          ) : (
            <EmptyStateCopySkeletons />
          )}
        </div>
      )}
      {
        <SearchBar
          focusOnMount
          transparentWhenCentered
          className={classNames(css.searchBar, {
            [css.hidden]:
              !isMobile && !uiPreferences.showSearchBarWhileSelectingMoments && isSelectingMoments,
            [css.searchBarSlideUp]:
              !isMobile && uiPreferences.showSearchBarWhileSelectingMoments && isSelectingMoments,
          })}
        />
      }
      {hasSearchTerms && (hasResults || isUpdating) && !isMobile && <SearchHeader />}
      {/* Three answers to "the search settled", most specific first.
          `shouldSuggestTranscriptSearch` stays ahead of `zeroResultReason` because it
          is the sharper answer for a scope that cannot serve visual results at all,
          and it names the fix in one sentence.

          They *can* both be true — an empty transcript-only library satisfies each,
          since `empty-scope` reads `stats.count === 0` on resolved data rather than
          the absence of it — so the tie is broken in the store: the suggestion stands
          down when nothing in scope has media, because transcript-only-ness isn't why
          an empty library returned nothing. Don't reorder this to compensate; the
          other ordering is wrong for a transcript-only library that *does* have media.

          Falling through to `MomentGrid` means there is something to render, or a
          search still in flight. */}
      {hasSearchTerms &&
        (librarySearch.shouldSuggestTranscriptSearch ? (
          <TranscriptSearchSuggestion />
        ) : librarySearch.zeroResultReason ? (
          <NoMomentsFound
            reason={librarySearch.zeroResultReason}
            onAddMedia={(libraryId) => {
              setAddMediaTarget(libraryId);
              addMediaModalRef.current?.open();
            }}
          />
        ) : (
          <MomentGrid />
        ))}
      {/* Mounted unconditionally rather than beside the empty state so opening it
          doesn't race the state that offered it: adding media changes the scope's
          stats, which retires `empty-scope` and would unmount the modal mid-upload.

          `destination` is set by the empty state when it can name one library — the
          state is a fact about the *search scope*, so defaulting to the default
          library could put the media somewhere the search isn't looking. Undefined
          falls back to that default, matching the Workspace signpost. */}
      <AddMediaModal
        ref={addMediaModalRef}
        destination={addMediaTarget ? { libraryId: addMediaTarget } : undefined}
      />
    </div>
  );
});

export const SearchPage = observer(function SearchPage() {
  return (
    <AppPage header={MOMENTS_HEADER}>
      <SearchParamsInURL />
      <_SearchPage />
    </AppPage>
  );
});
